/**
 * Quem atende um canal (29/09/2026) — o passo "atendentes" do assistente, e o
 * mesmo formulário no botão "Atendentes" de cada canal da lista.
 *
 * Uma caixa de marcar por pessoa, com a foto. Salvar troca a lista INTEIRA: é
 * o que a tela mostra, e é o que o motor faz.
 *
 * Quem ainda não tem acesso ao atendimento não aparece, e a tela diz quantas
 * pessoas são — senão alguém procura um nome que não está ali e conclui que a
 * lista está quebrada.
 */

import { useEffect, useState, type ReactNode } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Avatar, Botao } from '@/shared/ui';
import { Estado } from '@/shared/ui/estados/estados';
import { Faixa } from '@/shared/ui/estados/faixa';
import { useApi, useAtendentesDoCanal } from '@/shared/api/consultas';
import { chaves } from '@/shared/api/chaves';
import { recadoDeErro } from '@/shared/api/recado-de-erro';

export function AtendentesDoCanal({
  inboxId,
  nomeDoCanal,
  rotuloDoSalvar = 'Salvar',
  aoSalvar,
  outrasAcoes,
}: {
  inboxId: string;
  nomeDoCanal: string;
  rotuloDoSalvar?: string;
  aoSalvar?: () => void;
  /** Botões ao lado do salvar: "Cancelar" no diálogo, nada no assistente. */
  outrasAcoes?: ReactNode;
}): ReactNode {
  const api = useApi();
  const cache = useQueryClient();
  const consulta = useAtendentesDoCanal(inboxId);
  const [escolhidos, definirEscolhidos] = useState<ReadonlySet<string> | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  // A marcação começa pelo que o motor diz, uma vez; depois é da pessoa.
  useEffect(() => {
    if (consulta.data && escolhidos === null) {
      definirEscolhidos(
        new Set(consulta.data.itens.filter((p) => p.atende).map((p) => p.usuario_id)),
      );
    }
  }, [consulta.data, escolhidos]);

  const salvar = useMutation({
    mutationFn: () =>
      api!.definirAtendentes({
        caminho: { id: inboxId },
        corpo: { usuario_ids: [...(escolhidos ?? [])] },
      }),
    onMutate: () => definirErro(null),
    onSuccess: (lido) => {
      cache.setQueryData(chaves.atendentesDoCanal(inboxId), lido);
      aoSalvar?.();
    },
    onError: (falha: unknown) =>
      definirErro(
        recadoDeErro(falha, { generica: 'Não foi possível salvar quem atende. Tente de novo.' }),
      ),
  });

  const nenhum = (escolhidos?.size ?? 0) === 0;

  return (
    <Estado consulta={consulta} forma="lista" linhas={3}>
      {(lido) => (
        <div className="atendentes-canal">
          {lido.itens.length === 0 ? (
            <p className="admin__apoio">
              Ninguém nesta organização tem acesso ao atendimento ainda. Dê o acesso em Pessoas, e
              volte aqui.
            </p>
          ) : (
            <fieldset className="atendentes-canal__grupo">
              <legend className="atendentes-canal__legenda">Quem atende {nomeDoCanal}</legend>
              <ul className="atendentes-canal__lista">
                {lido.itens.map((pessoa) => {
                  const marcado = escolhidos?.has(pessoa.usuario_id) ?? pessoa.atende;
                  return (
                    <li key={pessoa.usuario_id}>
                      <label className="atendentes-canal__pessoa">
                        <input
                          type="checkbox"
                          checked={marcado}
                          onChange={(e) => {
                            const proximo = new Set(escolhidos ?? []);
                            if (e.target.checked) proximo.add(pessoa.usuario_id);
                            else proximo.delete(pessoa.usuario_id);
                            definirEscolhidos(proximo);
                          }}
                        />
                        <Avatar nome={pessoa.nome} tamanho={28} foto={pessoa.foto_url} />
                        <span>{pessoa.nome}</span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            </fieldset>
          )}

          {lido.sem_acesso_ao_atendimento > 0 && (
            <p className="admin__apoio">
              {lido.sem_acesso_ao_atendimento === 1
                ? '1 pessoa ainda não tem acesso ao atendimento e não aparece aqui.'
                : `${lido.sem_acesso_ao_atendimento} pessoas ainda não têm acesso ao atendimento e não aparecem aqui.`}
            </p>
          )}

          <div className="passos-canal__acoes">
            <Botao
              variante="primario"
              desabilitado={nenhum || salvar.isPending || api === null}
              motivo={
                api === null
                  ? 'Sem conexão agora. Tente de novo em instantes.'
                  : nenhum
                    ? 'Escolha ao menos uma pessoa: sem ninguém, as conversas chegam e ficam sem dono.'
                    : 'Salvando…'
              }
              onClick={() => salvar.mutate()}
            >
              {salvar.isPending ? 'Salvando…' : rotuloDoSalvar}
            </Botao>
            {outrasAcoes}
          </div>

          {erro && <Faixa tom="bloqueado">{erro}</Faixa>}
        </div>
      )}
    </Estado>
  );
}
